import { Card, CardBody, CardHeader } from "reactstrap";

import RichTextWidgetCreate from "../Widgets/RichText/RichTextWidgetCreate";
import { ReusableViewReferenceFields } from "../../types/PageInterfaces";
import { RecordSummaryWidget } from "../Widgets/RecordSummaryWidget";
import { StageProgressWidget } from "../Widgets/StageProgressWidget";
import RichTextWidget from "../Widgets/RichText/RichTextWidget";
import ManageMultiSubitem from "./Subitems/ManageMultiSubitem";
import { Module } from "../../types/TenantInterfaces";
import InfoToolip from "../../elements/InfoToolip";
import FieldUtils from "../../utils/FieldUtils";

interface Props {
  section: any;
  record?: Record<string, any>;
  module: Module | undefined;
  createMode?: boolean;
  values?: any;
  handleChange?: any;
  prefill?: any;
  readonlyPrefill?: any;
  variableValues?: any;
  readOnly?: boolean;
  marginBottom?: boolean;
  useCard?: boolean;
  isCard?: boolean;
  parentRecord?: any;
  addSubitem?: any;
  removeSubitem?: any;
  publicForm?: boolean;
  headerOptions?: React.ReactNode;
  detailViewLayoutID?: string;
  payload?: any;
  reusableViewReferenceFields?: ReusableViewReferenceFields;
}

// All Props below createMode are only needed when createMode is true.
const SectionRenderer = ({
  section,
  record,
  module,
  headerOptions,
  createMode = false,
  values = null,
  handleChange = null,
  prefill = null,
  readonlyPrefill = null, // Prefill the overrides prefill prop above, and prevents the user from changing the value.
  variableValues = null,
  readOnly = false,
  marginBottom = true,
  useCard = true,
  isCard = false,
  parentRecord = null,
  addSubitem,
  removeSubitem,
  publicForm = false,
  detailViewLayoutID,
  payload,
  reusableViewReferenceFields,
}: Props) => {
  if (!module || !section) return null;
  if (!createMode && !record) return null;

  const convertFields = (field: any) => {
    if (typeof field.Field !== "string") return field;

    const apiName = field.Field;
    let newField;

    if (createMode) {
      newField = FieldUtils.getFieldFromModule(
        module,
        apiName,
        true,
        prefill,
        variableValues,
        readonlyPrefill
      );
    } else {
      newField = FieldUtils.getFieldFromModule(module, apiName);
    }

    return { ...field, Field: newField };
  };

  if (createMode && section.Type === "MultiSubitem") {
    return (
      <ManageMultiSubitem
        module={module}
        section={section}
        fieldSchema={section.ParentField}
        convertFields={convertFields}
        createMode={createMode}
        values={values}
        handleChange={handleChange}
        record={record}
        readOnly={readOnly}
        addSubitem={addSubitem}
        prefill={prefill}
        removeSubitem={removeSubitem}
        publicForm={publicForm}
        parentRecord={undefined}
        reusableViewReferenceFields={reusableViewReferenceFields}
      />
    );
  }

  if (
    section.Type === "RecordSummarySection" ||
    section.Type === "Subitem" ||
    section.Type === "MultiSubitem"
  ) {
    return (
      <RecordSummaryWidget
        parentRecord={
          ["Subitem", "MultiSubitem"].includes(section.Type)
            ? parentRecord
            : undefined
        }
        module={module}
        section={section}
        convertFields={convertFields}
        createMode={createMode}
        values={values}
        handleChange={handleChange}
        record={record}
        readOnly={readOnly}
        marginBottom={marginBottom}
        useCard={useCard}
        isCard={isCard}
        headerOptions={headerOptions}
        payload={payload}
        reusableViewReferenceFields={reusableViewReferenceFields}
      />
    );
  }

  if (!createMode && section.Type === "StageProgress") {
    return (
      <Card className="border border-superlight shadow-sm mt-3 mb-4">
        <CardHeader className="px-3 py-2 d-flex justify-content-between">
          <div className="d-flex align-items-center">
            {section.Title && <h3 className="mb-0">{section.Title}</h3>}
            {section?.Description && (
              <InfoToolip
                text={section?.Description}
                iconCSS="ml-2"
                size={18}
              />
            )}
          </div>
          <div className="d-flex">{headerOptions}</div>
        </CardHeader>
        <CardBody className="p-3">
          <StageProgressWidget
            module={module}
            section={section}
            record={record}
            detailViewLayoutID={detailViewLayoutID}
          />
        </CardBody>
      </Card>
    );
  }

  if (section.Type === "RichTextSection") {
    if (createMode) {
      const rtField = module?.Fields?.find((f) => f.APIName === section.Field);

      if (!rtField) return null;

      return (
        <RichTextWidgetCreate
          field={rtField}
          key={values?.[section.Field]}
          title={section.Title}
          description={section.Description}
          handleChange={handleChange}
          startingRichText={values?.[section?.Field]}
        />
      );
    }

    const savedRichText = record?.[section?.Field]?.RichText;

    return (
      <RichTextWidget
        key={savedRichText}
        savedRichText={savedRichText}
        recordID={record?.id}
        field={module?.Fields?.find((f: any) => f.APIName === section.Field)}
        moduleAPIName={record?._ModuleAPIName}
        title={section.Title}
        description={section.Description}
        headerOptions={headerOptions}
      />
    );
  }

  return null;
};

export default SectionRenderer;
